import { debug } from '@/utils/debug';
import { meaningfulStatus } from '@/utils/formatEnum';
import type { AppCampaignConfig, AppConfiguration } from '@/types/api';
import type { Campaign, PlatformSource } from '@/types/campaign';
import type { MetaCampaign } from '@/types/metaCampaign';

// `PlatformSource` is re-exported from its canonical home in
// `@/types/campaign` so consumers of this module don't need to import from
// two locations. Single source of truth for the discriminator union —
// adding a third platform: widen the type in `@/types/campaign`, then add
// an overlay function below in the same shape as
// `mergeMetaCampaignWithSavedEntry`.
export type { PlatformSource };

/**
 * Centralized per-platform lookup into `app_config.configuration.campaign_list`.
 *
 * The Account page (and any consumer that overlays saved state onto fresh API
 * data) needs the platform_source filter applied consistently — without it, a
 * Meta entry whose campaign_id happens to collide with a Google one would
 * silently leak across platforms in the merge. Pre-A3 entries lack the stamp;
 * backend helpers default missing → 'Google Ads', so we match that fallback.
 *
 * Mirrors the inclusion-only filter the platform stores use in
 * `toConfigSlice` / `hydrateFromConfig` (campaignSettingsGoogleAdsStore,
 * campaignSettingsMetaAdsStore). Adding a third platform: widen
 * `PlatformSource` to include its discriminator value, add an overlay
 * function in the same shape as `mergeMetaCampaignWithSavedEntry`, and the
 * lookup itself doesn't change.
 */
export function findSavedEntryForPlatform(
  savedList: AppCampaignConfig[] | undefined,
  campaignId: string,
  platformSource: PlatformSource,
): AppCampaignConfig | undefined {
  if (!savedList) return undefined;
  const idStr = String(campaignId);
  return savedList.find((entry) => {
    if (String(entry.campaign_id) !== idStr) return false;
    const stamped = entry.platform_source ?? 'Google Ads';
    return stamped === platformSource;
  });
}

/**
 * Overlay saved-config state onto a fresh API Google campaign.
 *
 * Pattern: API wins for live data (metrics, google_ads_account_id), saved
 * config wins for structure (campaign_name, status, ads_group_list,
 * asset_group_list, landing_pages, campaign_type, flouri_optimize_budget,
 * budget, target_density).
 *
 * Extracted as-is from the Account page's inline merge so behavior is
 * preserved. The PMax detection + meaningfulStatus dance below are
 * historical Google-side quirks; Meta has its own equivalents in
 * mergeMetaCampaignWithSavedEntry.
 */
export function mergeGoogleCampaignWithSavedEntry(
  apiCampaign: Campaign,
  configCampaign: AppCampaignConfig | undefined,
): Campaign {
  if (!configCampaign) return apiCampaign;

  // Determine campaign type - prefer config, fallback to metrics.
  // Also check for asset_group_list presence as indicator of PMax.
  let finalCampaignType = configCampaign.campaign_type || apiCampaign.campaign_type;
  if (
    configCampaign.asset_group_list &&
    configCampaign.asset_group_list.length > 0 &&
    !finalCampaignType?.includes('PERFORMANCE_MAX')
  ) {
    finalCampaignType = 'PERFORMANCE_MAX';
    debug.log(
      `mergeGoogleCampaignWithSavedEntry: Corrected campaign_type to PERFORMANCE_MAX for ${apiCampaign.campaign_id} based on asset_group_list presence`,
    );
  }

  return {
    ...apiCampaign,
    campaign_name: configCampaign.campaign_name || apiCampaign.campaign_name,
    // `campaign_status` is what the backend writes at the campaign level
    // (`_update_campaign_status_in_config` in campaigns.py / account_configuration.py
    // / meta_campaigns.py). `configCampaign.status` is a legacy field an older
    // frontend `saveConfiguration` used to write; if both are present,
    // `campaign_status` is authoritative. `meaningfulStatus` treats sentinels
    // ('UNKNOWN' / 'UNSPECIFIED') as missing so a regenerated 'UNKNOWN' doesn't
    // override a valid legacy `status` or live `apiCampaign.status`.
    status: (meaningfulStatus(configCampaign.campaign_status)
      || meaningfulStatus(configCampaign.status)
      || apiCampaign.status) as Campaign['status'],
    // `??` not direct assignment for these optional fields — a legacy /
    // partial saved-config entry that omits them would otherwise write
    // `undefined` over live API data after the `...apiCampaign` spread.
    // (`flouri_optimize_budget: false` and `budget: 0` are valid values
    // so `??` is required — `||` would treat them as missing.)
    ads_group_list: (configCampaign.ads_group_list ?? apiCampaign.ads_group_list) as Campaign['ads_group_list'],
    asset_group_list: (configCampaign.asset_group_list ?? apiCampaign.asset_group_list) as Campaign['asset_group_list'],
    landing_pages: (configCampaign.landing_pages ?? apiCampaign.landing_pages) as Campaign['landing_pages'],
    campaign_type: finalCampaignType,
    // API wins for google_ads_account_id (live).
    google_ads_account_id: apiCampaign.google_ads_account_id || configCampaign.google_ads_account_id,
    flouri_optimize_budget: configCampaign.flouri_optimize_budget ?? apiCampaign.flouri_optimize_budget,
    budget: configCampaign.budget ?? apiCampaign.budget,
    target_density: configCampaign.target_density ?? apiCampaign.target_density,
  };
}

/**
 * Overlay saved-config state onto a fresh API Meta campaign.
 *
 * Same pattern as the Google overlay: API wins for live data (metrics,
 * meta_ad_account_id, timestamps), saved config wins for structural fields
 * (campaign_name, campaign_status, objective, daily/lifetime_budget,
 * flouri_optimize_budget, etc.).
 *
 * Meta-specific notes:
 * - `campaign_status` uses the same `meaningfulStatus` sentinel guard as
 *   Google so a regenerated 'UNKNOWN' / 'UNSPECIFIED' doesn't shadow a valid
 *   live status.
 * - `ad_sets` is not on the list-level MetaCampaign type (it lives on
 *   MetaCampaignDetail for the detail page), so we don't overlay it here.
 *   The detail page's separate read path picks up ad_sets from saved config
 *   via `buildMetaCampaignFromConfig` in useCampaignFromCache.ts.
 * - `flouri_optimize_budget` is wired through but the toggle UI on Meta
 *   cards is disabled until A5 (Meta budget activation).
 */
export function mergeMetaCampaignWithSavedEntry(
  apiCampaign: MetaCampaign,
  configCampaign: AppCampaignConfig | undefined,
): MetaCampaign {
  if (!configCampaign) return apiCampaign;

  return {
    ...apiCampaign,
    campaign_name: configCampaign.campaign_name || apiCampaign.campaign_name,
    campaign_status:
      (meaningfulStatus(configCampaign.campaign_status)
      || meaningfulStatus(configCampaign.status)
      || apiCampaign.campaign_status) as MetaCampaign['campaign_status'],
    objective: configCampaign.objective ?? apiCampaign.objective,
    daily_budget: configCampaign.daily_budget ?? apiCampaign.daily_budget,
    lifetime_budget: configCampaign.lifetime_budget ?? apiCampaign.lifetime_budget,
    bid_strategy: configCampaign.bid_strategy ?? apiCampaign.bid_strategy,
    buying_type: configCampaign.buying_type ?? apiCampaign.buying_type,
    is_cbo: configCampaign.is_cbo ?? apiCampaign.is_cbo,
    special_ad_categories: configCampaign.special_ad_categories ?? apiCampaign.special_ad_categories,
    flouri_optimize_budget:
      configCampaign.flouri_optimize_budget ?? apiCampaign.flouri_optimize_budget,
    // API wins for meta_ad_account_id (live). `configCampaign.meta_ad_account_id`
    // can be undefined on legacy entries; MetaCampaign.meta_ad_account_id is
    // string | null, so coalesce.
    meta_ad_account_id:
      apiCampaign.meta_ad_account_id ?? configCampaign.meta_ad_account_id ?? null,
    // metrics / start_time / stop_time / created_at / updated_at / platform_source
    // are preserved by the apiCampaign spread above.
  };
}

/**
 * Convenience overlay: find the platform-specific saved entry and apply the
 * Google overlay. Returns the API campaign unchanged when there's no saved
 * entry or no saved list at all.
 */
export function applyGoogleOverlay(
  apiCampaign: Campaign,
  savedConfig: AppConfiguration | undefined | null,
): Campaign {
  const entry = findSavedEntryForPlatform(
    savedConfig?.configuration?.campaign_list,
    apiCampaign.campaign_id,
    'Google Ads',
  );
  return mergeGoogleCampaignWithSavedEntry(apiCampaign, entry);
}

/**
 * Convenience overlay: find the platform-specific saved entry and apply the
 * Meta overlay. Returns the API campaign unchanged when there's no saved
 * entry or no saved list at all.
 */
export function applyMetaOverlay(
  apiCampaign: MetaCampaign,
  savedConfig: AppConfiguration | undefined | null,
): MetaCampaign {
  const entry = findSavedEntryForPlatform(
    savedConfig?.configuration?.campaign_list,
    apiCampaign.campaign_id,
    'Meta Ads',
  );
  return mergeMetaCampaignWithSavedEntry(apiCampaign, entry);
}
